import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment,
  Link,
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import OtpInput from 'react-otp-input';
import { request } from '../../Utils/request';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user);
  const [email, setEmail] = useState(user?.user?.email || '');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  /** When email is already verified, user can tap "Update Another Email" to verify a new address (same pattern as phone). */
  const [overrideVerified, setOverrideVerified] = useState(false);

  const isEmailVerifiedFromUser =
    user?.user?.emailVerification?.isVerified === true ||
    (user?.user as { emailVerified?: boolean } | undefined)?.emailVerified === true;

  const isEmailVerified = !overrideVerified && isEmailVerifiedFromUser;

  const handleUpdateAnotherEmail = () => {
    setOverrideVerified(true);
    setEmail('');
    setOtp('');
    setStep('email');
    setError('');
    setSuccess('');
  };

  const handleEmailSubmit = async () => {
    if (!email.trim() || !emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await request('/auth/change-email/request', {
        method: 'POST',
        authorization: true,
        body: { newEmail: email.trim() },
      });
      setSuccess('OTP sent successfully to your email');
      setStep('otp');
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async () => {
    if (!otp.trim()) {
      setError('Please enter the OTP');
      return;
    }
    if (otp.length !== 6) {
      setError('OTP must be 6 digits');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await request('/auth/change-email/verify', {
        method: 'POST',
        authorization: true,
        body: { otp, email: email.trim() },
      });
      setSuccess('Email verified successfully!');
      setTimeout(() => {
        navigate('/profile');
      }, 1200);
    } catch (err: any) {
      setError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError('');
    try {
      await request('/auth/change-email/resend-otp', {
        method: 'POST',
        authorization: true,
        body: { newEmail: email.trim() },
      });
      setSuccess('OTP resent successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (isEmailVerified) {
    return (
      <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
        <TopNavBar title="Email Verification" canGoBack />
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CheckIcon sx={{ fontSize: 80, color: '#4CAF50', mb: 3 }} />
            <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
              Email Already Verified
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your email {user?.user?.email || ''} is already verified.
            </Typography>
            <Button
              variant="contained"
              onClick={handleUpdateAnotherEmail}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                backgroundColor: '#F57C00',
              }}
            >
              Update Another Email
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      <TopNavBar title={step === 'email' ? 'Verify Email' : 'Verify OTP'} canGoBack />

      <Container maxWidth="sm" sx={{ py: 4 }}>
        {step === 'email' ? (
          <Stack spacing={3}>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              We will send a verification code to your email
            </Typography>

            <TextField
              fullWidth
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
              error={!!error && error.toLowerCase().includes('email')}
              helperText={error.toLowerCase().includes('email') ? error : ''}
            />

            {error && !error.toLowerCase().includes('email') && (
              <Alert severity="error">{error}</Alert>
            )}
            {success && <Alert severity="success">{success}</Alert>}

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleEmailSubmit}
              disabled={loading || !email.trim()}
              sx={{
                py: 2,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                backgroundColor: '#F57C00',
                '&:hover': { backgroundColor: '#E65100' },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
            </Button>
          </Stack>
        ) : (
          <Stack spacing={4}>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              Enter the 6-digit code sent to your email
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <OtpInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                renderInput={(props) => (
                  <input
                    {...props}
                    style={{
                      flex: '1 1 0',
                      minWidth: '40px',
                      maxWidth: '55px',
                      width: '100%',
                      height: '50px',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      border: '2px solid #F57C00',
                      borderRadius: '8px',
                      outline: 'none',
                      backgroundColor: 'white',
                      color: '#333',
                      boxSizing: 'border-box',
                    }}
                  />
                )}
                containerStyle={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '6px',
                  width: '100%',
                  maxWidth: '420px',
                }}
                shouldAutoFocus
              />
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleOtpSubmit}
              disabled={loading || otp.length !== 6}
              sx={{
                py: 2,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                backgroundColor: '#F57C00',
                '&:hover': { backgroundColor: '#E65100' },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Email'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'inline' }}>
                Didn't receive the OTP?{' '}
              </Typography>
              <Link
                component="button"
                variant="body2"
                onClick={handleResendOtp}
                disabled={loading}
                sx={{ color: '#F57C00', textDecoration: 'none', fontWeight: 'bold' }}
              >
                Resend OTP
              </Link>
            </Box>
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default EmailVerification;
